/**
 * Kademeli Müşteri → Bölüm → PO → PO No seçimi (Atanacak Mesailer'deki seçimden ortak yardımcıya alındı, 18.10.2026;
 * Ek Mesai Talepleri penceresi de kullanır). Zincir tek bir PO Eşleştirme satırına (companyPoId) çözülür.
 */

export interface PoSatiri {
    companyPoId: string
    customerId?: string | null
    customerName?: string | null
    /** Bölüm kimliği (depo eşleşmesi, #507). */
    divisionId?: string | null
    divisionName?: string | null
    poName?: string | null
    poNo?: string | null
}

export interface KademeSecenegi {
    label: string
    value: string
}

/** Seçili değerler: musteri = müşteri anahtarı, bolum / po = ad (boşsa BOS), companyPoId = PO No satırı. */
export interface KademeSecimi {
    musteri?: string
    bolum?: string
    po?: string
    companyPoId?: string
}

export const BOS = '__bos__';
export const BOS_ETIKET = '(Boş)';

export const musteriAnahtari = (x: PoSatiri) => x.customerId || ('ad:' + (x.customerName ?? ''));
export const kademeDegeri = (s: string | null | undefined) => s && s.trim() != '' ? s : BOS;
export const kademeEtiketi = (s: string | null | undefined) => s && s.trim() != '' ? s : BOS_ETIKET;

/** Tekil seçenekler (ilk görülen korunur), etikete göre Türkçe sıralı. */
export const tekilSecenekler = (l: KademeSecenegi[]) => {
    var m = new Map<string, KademeSecenegi>();
    l.forEach(x => { if (!m.has(x.value)) m.set(x.value, x); });
    return Array.from(m.values()).sort((a, b) => a.label.localeCompare(b.label, 'tr'));
}

const musteriSatirlari = (satirlar: PoSatiri[], s: KademeSecimi) => satirlar.filter(x => musteriAnahtari(x) == s.musteri);
const bolumSatirlari = (satirlar: PoSatiri[], s: KademeSecimi) => musteriSatirlari(satirlar, s).filter(x => kademeDegeri(x.divisionName) == s.bolum);
const poSatirlariOf = (satirlar: PoSatiri[], s: KademeSecimi) => bolumSatirlari(satirlar, s).filter(x => kademeDegeri(x.poName) == s.po);

export const musteriSecenekleri = (satirlar: PoSatiri[]) =>
    tekilSecenekler(satirlar.map(x => ({ label: kademeEtiketi(x.customerName), value: musteriAnahtari(x) })));

export const bolumSecenekleri = (satirlar: PoSatiri[], s: KademeSecimi) =>
    tekilSecenekler(musteriSatirlari(satirlar, s).map(x => ({ label: kademeEtiketi(x.divisionName), value: kademeDegeri(x.divisionName) })));

export const poSecenekleri = (satirlar: PoSatiri[], s: KademeSecimi) =>
    tekilSecenekler(bolumSatirlari(satirlar, s).map(x => ({ label: kademeEtiketi(x.poName), value: kademeDegeri(x.poName) })));

export const poNoSecenekleri = (satirlar: PoSatiri[], s: KademeSecimi) =>
    tekilSecenekler(poSatirlariOf(satirlar, s).map(x => ({ label: kademeEtiketi(x.poNo), value: x.companyPoId })));

/** Bir seviyede tek seçenek kalırsa kendiliğinden seçilir; zincir PO No'ya (companyPoId) kadar ilerler. */
export const kademeyiTamamla = <T extends KademeSecimi>(satirlar: PoSatiri[], s: T): T => {
    var r = { ...s };

    if (r.musteri && !r.bolum) {
        var b = bolumSecenekleri(satirlar, r);
        if (b.length == 1) r.bolum = b[0].value;
    }

    if (r.bolum && !r.po) {
        var p = poSecenekleri(satirlar, r);
        if (p.length == 1) r.po = p[0].value;
    }

    if (r.po && !r.companyPoId) {
        var n = poNoSecenekleri(satirlar, r);
        if (n.length == 1) r.companyPoId = n[0].value;
    }

    return r;
}

/** Seviye değişince alt seviyeler sıfırlanır (ve tek seçenekli seviyeler kendiliğinden dolar). */
export const musteriSec = <T extends KademeSecimi>(satirlar: PoSatiri[], s: T, musteri: string | undefined): T =>
    kademeyiTamamla(satirlar, { ...s, musteri, bolum: undefined, po: undefined, companyPoId: undefined });

export const bolumSec = <T extends KademeSecimi>(satirlar: PoSatiri[], s: T, bolum: string | undefined): T =>
    kademeyiTamamla(satirlar, { ...s, bolum, po: undefined, companyPoId: undefined });

export const poSec = <T extends KademeSecimi>(satirlar: PoSatiri[], s: T, po: string | undefined): T =>
    kademeyiTamamla(satirlar, { ...s, po, companyPoId: undefined });

/** Kayıtlı companyPoId'den zinciri geri kurar (güncelleme penceresi açılırken). Satır yoksa boş seçim. */
export const kademeSecimiOf = (satirlar: PoSatiri[], companyPoId: string | null | undefined): KademeSecimi => {
    var x = companyPoId ? satirlar.find(r => r.companyPoId == companyPoId) : undefined;

    if (!x)
        return {};

    return { musteri: musteriAnahtari(x), bolum: kademeDegeri(x.divisionName), po: kademeDegeri(x.poName), companyPoId: x.companyPoId };
}
